import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    TextField,
    Button,
    Grid,
    Box,
    FormControl,
    FormHelperText,
    Autocomplete,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { 
    ajaxSavePartComponent, 
    ajaxGetPartComponent, 
    ajaxDeletePartComponent, 
    ajaxDeletePartComponentImage, 
    ajaxGetPartComponentImageUrl,
    ajaxEnablePartComponent,
    ajaxDisablePartComponent
} from '../../services/partComponentService';
import { noty, downloadFile, getAllCurrencyList } from '../../helpers';
import Page from '../../components/Page';
import StyledCard from '../../components/StyledCard';
import StyledCardFormContent from '../../components/StyledCardFormContent';
import StyledMainGrid from '../../components/StyledMainGrid';
import FileUploader from '../../components/FileUploader';
import validator from 'validator';
import ConfirmationButton from '../../components/ConfirmationButton';
import PartComponentStatisticDialog from '../../components/PartComponentStatisticDialog';

export default function PartComponent(props) {
    const navigate = useNavigate();
    const { id } = useParams();
    const [component, setComponent] = useState({
        description: '',
        manufacturer: '',
        manuf_part_number: '',
        price: 0,
        local_price: 0,
        local_currency: 'USD',
        part_image: '',
        part_image_name: '',
        isNewImg: true,
        active: 1,
        unit: 'pcs'
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isProcessingImg, setIsProcessingImg] = useState(false);
    const [isEnableDisable, setIsEnableDisable] = useState(false);
    const [partImgUrl, setPartImgUrl] = useState('');
    const [openStatistic, setOpenStatistic] = useState(false);

    const allFields = useMemo(
        () => [
            {
                key: 'manuf_part_number',
                type: 'text',
                label: 'Manufacturer Part Number',
                required: true,
                disabled: !props.add,
                gridProps: { xs: 12, md: 12 }
            },
            {
                key: 'manufacturer',
                type: 'text',
                label: 'Manufacturer',
                required: true,
                gridProps: { xs: 12, md: 12 }
            },
            {
                key: 'description',
                type: 'text',
                label: 'Description',
                multiline: true,
                rows: 4,
                gridProps: { xs: 12 }
            },
            {
                key: 'price',
                type: 'number',
                label: 'Price in USD',
                required: true,
                gridProps: { xs: 12, md: 12 }
            },
            {
                key: 'local_price',
                type: 'number',
                label: 'Price in Local Currency',
                gridProps: { xs: 12, md: 6 }
            },
            {
                key: 'local_currency',
                type: 'autocomplete',
                label: 'Local Currency',
                dataset: getAllCurrencyList().map(c => ({ value: c, label: c })),
                required: true,
                gridProps: { xs: 12, md: 6 }
            },
            {
                key: 'unit',
                type: 'text',
                label: 'Unit',
                required: true,
                gridProps: { xs: 12, md: 6 }
            },
            {
                key: 'part_image',
                type: 'file',
                label: 'Part Image',
                required: false,
                accept: 'image/*',
                gridProps: { xs: 12, md: 6 }
            }
        ],
        [props.add]
    );

    const requiredFields = useMemo(() => ['manufacturer', 'manuf_part_number', 'price', 'unit', 'local_currency'], []);
    const floatFields = useMemo(() => ['price', 'local_price'], []);

    const [errors, setErrors] = useState({});
    const [duplicateErrors, setDuplicateErrors] = useState({});
    const [submitOnce, setSubmitOnce] = useState(false);

    const handleImageChange = (e) => {
        let key = e.target.name;
        let value = e.target.files[0];
        setComponent(prev => ({
            ...prev,
            [key]: value,
            part_image_name: value.name,
            isNewImg: true
        }));
    };

    const handleDownloadImage = () => {
        setIsProcessingImg(true);
        downloadFile({
            params: {
                id: component.id,
                fileType: 'part/part_image'
            },
            fileName: component.part_image_name,
            fileExt: 'png'
        }).then(() => {
            setIsProcessingImg(false);
        }).catch(() => {
            setIsProcessingImg(false);
        });
    };

    const handleDeleteImage = () => {
        setIsProcessingImg(true);
        ajaxDeletePartComponentImage(component.id)
            .then((res) => {
                let copyComponent = { ...component };
                copyComponent.part_image = res.data.part_image;
                copyComponent.isNewImg = true;
                setComponent(copyComponent);
                setIsProcessingImg(false);
            })
            .catch(() => {
                setIsProcessingImg(false);
            });
    };

    useEffect(() => {
        if (!props.add && id) {
            ajaxGetPartComponent(id)
                .then((response) => {
                    if (response.data) {
                        const data = {
                            ...response.data,
                            price: Number(response.data.price)
                        };
                        setComponent(data);
                        if (data.part_image) {
                            ajaxGetPartComponentImageUrl(id)
                                .then((url) => {
                                    setPartImgUrl(url);
                                });
                        }
                    } else {
                        navigate('/partComponents');
                    }
                })
                .catch((error) => {
                    navigate('/partComponents');
                });
        }
    }, [id, props.add, navigate]);

    const validateFields = useCallback(() => {
        const fields = { ...component };
        let newErrors = {};
        let result = true;

        for (let index in allFields) {
            let key = allFields[index].key;
            let min = allFields[index].min;
            let max = allFields[index].max;
            let fieldValue = fields[key] !== undefined && fields[key] !== null ? fields[key] + '' : '';

            if (requiredFields.includes(key) && validator.isEmpty(fieldValue, { ignore_whitespace: true })) {
                newErrors[key] = 'required field';
                result = false;
            } else if (fieldValue && floatFields.includes(key) && !validator.isFloat(fieldValue)) {
                newErrors[key] = 'Please enter a valid number';
                result = false;
            } else if (fieldValue && !isNaN(min) && !isNaN(max) && (isNaN(fieldValue) || parseFloat(fieldValue) < min || parseFloat(fieldValue) > max)) {
                newErrors[key] = `Please enter a value between ${min} and ${max}.`;
                result = false;
            }
        }

        setErrors(newErrors);
        return result;
    }, [component, allFields, requiredFields, floatFields]);

    useEffect(() => {
        if (!props.add) {
            validateFields();
        } else if (props.add && submitOnce) {
            validateFields();
        }
    }, [validateFields, props.add, submitOnce]);

    const handleFieldChange = (event, val) => {
        let value = val !== undefined ? val : event.target.value,
            key = typeof event === 'string' ? event : event.target.name,
            field = allFields.find(f => f.key === key);

        if (field && field.type === 'autocomplete') {
            setComponent({ ...component, [key]: value ? value.value : null });
        } else {
            setComponent({ ...component, [key]: value });
        }

        // Clear duplicate error for the changed field
        if (duplicateErrors[key]) {
            setDuplicateErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitOnce(true);
        if (!validateFields()) return false;
        if(Object.keys(duplicateErrors).filter(k => !!duplicateErrors[k]).length > 0) {
            return false;
        }

        const submitData = {
            ...component,
            price: Number(component.price)
        };

        if (!props.add && component.id) {
            ajaxSavePartComponent(submitData)
                .then(() => {
                    noty('Component updated successfully', 'success');
                    navigate('/partComponents');
                })
                .catch((e) => {
                    if (e.cause) {
                        let errorResult = {};
                        for (let k in e.cause) {
                            if (allFields.filter(f => f.key === k).length > 0) {
                                errorResult[k] = e.cause[k];
                            }
                        }
                        setDuplicateErrors(errorResult);
                    }
                });
        } else {
            ajaxSavePartComponent(submitData)
                .then(() => {
                    noty('Component created successfully', 'success');
                    navigate('/partComponents');
                })
                .catch((e) => {
                    if (e.cause) {
                        let errorResult = {};
                        for (let k in e.cause) {
                            if (allFields.filter(f => f.key === k).length > 0) {
                                errorResult[k] = e.cause[k];
                            }
                        }
                        setDuplicateErrors(errorResult);
                    }
                });
        }
    };

    const handleDelete = () => {
        setIsDeleting(true);
        ajaxDeletePartComponent(component.id)
            .then(() => {
                noty('Component deleted successfully', 'success');
                navigate('/partComponents');
            })
            .catch(() => {
                setIsDeleting(false);
            });
    };

    const getFieldValue = (key) => {
        return component && component[key] !== undefined && component[key] !== null ? component[key] : '';
    };

    const getFieldErrorText = (key) => errors && errors[key] ? errors[key] : (duplicateErrors && duplicateErrors[key] ? duplicateErrors[key] : '');

    const checkFieldError = (key) => (errors && errors[key]) || (duplicateErrors && duplicateErrors[key]);

    const style = {
        button: {
            height: '40px',
            width: '150px',
            fontSize: '12px'
        },
        buttonSave: {
            height: '40px',
            width: '180px',
            fontSize: '12px'
        },
        bgImg: {
            width: '100%',
            maxHeight: '350px',
            objectFit: 'contain'
        }
    };

    const handleEnable = () => {
        setIsEnableDisable(true);
        ajaxEnablePartComponent(component.id)
            .then((res) => {
                setComponent({...component, active: res.data.active });
                setIsEnableDisable(false);
            })
            .catch((e) => {
                console.log(e.message);
                setIsEnableDisable(false)
            });
    };

    const handleDisable = () => {
        setIsEnableDisable(true);
        ajaxDisablePartComponent(component.id)
            .then((res) => {
                setComponent({...component, active: res.data.active });
                setIsEnableDisable(false);
            })
            .catch((e) => {
                console.log(e.message);
                setIsEnableDisable(false)
            });
    };

    const handleOpenStatistic = () => {
        setOpenStatistic(true);
    };

    const handleCloseStatistic = () => {
        setOpenStatistic(false);
    };

    return (
        <Page title="Add / Edit Part Component">
            <StyledCard>
                <StyledCardFormContent>
                    {!props.add && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '25px', mt: '-20px' }}>
                            <Link onClick={handleOpenStatistic}>View Statistics</Link>
                        </Box>
                    )}
                    <form onSubmit={handleSubmit}>
                        <StyledMainGrid columnSpacing>
                            {allFields.map((field) => (
                                <Grid item key={field.key} {...field.gridProps}>
                                    {field.type === 'text' && (
                                        <TextField
                                            name={field.key}
                                            label={field.label}
                                            value={getFieldValue(field.key)}
                                            onChange={handleFieldChange}
                                            fullWidth
                                            required={field.required}
                                            multiline={field.multiline}
                                            rows={field.rows}
                                            size="small"
                                            error={checkFieldError(field.key)}
                                            helperText={getFieldErrorText(field.key)}
                                            disabled={field.disabled}
                                        />
                                    )}
                                    {field.type === 'number' && (
                                        <TextField
                                            name={field.key}
                                            label={field.label}
                                            type="number"
                                            value={getFieldValue(field.key)}
                                            onChange={handleFieldChange}
                                            fullWidth
                                            required={field.required}
                                            size="small"
                                            error={checkFieldError(field.key)}
                                            helperText={getFieldErrorText(field.key)}
                                        />
                                    )}
                                    {field.type === 'autocomplete' && (
                                        <FormControl fullWidth error={checkFieldError(field.key)}>
                                            <Autocomplete
                                                sx={{ width: '100%' }}
                                                size="small"
                                                value={(field.dataset || []).find(o => o.value === getFieldValue(field.key)) || null}
                                                options={(field.dataset || [])}
                                                isOptionEqualToValue={(option, value) => value && option.value === value.value}
                                                getOptionLabel={(option) => option.label}
                                                onChange={(_, v) => handleFieldChange(field.key, v)}
                                                disabled={field.disabled}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={field.label}
                                                        required={field.required}
                                                        error={checkFieldError(field.key)}
                                                        helperText={getFieldErrorText(field.key)}
                                                    />
                                                )}
                                            />
                                        </FormControl>
                                    )}
                                    {field.type === 'file' && (
                                        <FormControl fullWidth error={checkFieldError(field.key)}>
                                            <FileUploader
                                                error={checkFieldError(field.key)}
                                                accept={field.accept}
                                                name={field.key}
                                                label={field.label}
                                                icon={<AttachFileIcon />}
                                                handleChange={handleImageChange}
                                                disabled={isProcessingImg}
                                            />
                                            <FormHelperText>{getFieldErrorText(field.key)}</FormHelperText>
                                        </FormControl>
                                    )}
                                </Grid>
                            ))}
                            {!props.add && component.part_image && !component.isNewImg && (
                                <React.Fragment>
                                    <Grid item xs={12}>
                                        <Box sx={{ mb: '10px' }}>
                                            <Button 
                                                variant="contained" 
                                                size="normal" 
                                                style={style.button} 
                                                disabled={isProcessingImg} 
                                                sx={{ mr: '20px' }} 
                                                onClick={handleDownloadImage}
                                            >
                                                Download Image
                                            </Button>
                                            <ConfirmationButton
                                                buttonProps={{ 
                                                    disabled: isProcessingImg, 
                                                    variant: 'contained', 
                                                    size: 'normal', 
                                                    style: style.button, 
                                                    sx: { backgroundColor: 'red' } 
                                                }}
                                                handleDialogClickYes={handleDeleteImage}
                                                buttonText="Delete Image"
                                                dialogText="Are you sure you want to delete the part image?"
                                                dialogYesText="Confirm"
                                                dialogNoText="Cancel"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ width: '50%' }}>
                                            <img 
                                                style={style.bgImg}
                                                alt="Part" 
                                                src={partImgUrl} 
                                            />
                                        </Box>
                                    </Grid>
                                </React.Fragment>
                            )}
                            <Grid item xs={12} />
                            {!props.add && (
                                <Grid item xs={12}>
                                    {!!component.active ?
                                        <Button 
                                            variant="contained" 
                                            size="normal" 
                                            disabled={isEnableDisable} 
                                            style={style.button} 
                                            color="error" 
                                            onClick={handleDisable}
                                        >
                                            Disable Component
                                        </Button>
                                        :
                                        <Button 
                                            variant="contained" 
                                            size="normal" 
                                            disabled={isEnableDisable} 
                                            style={style.button} 
                                            sx={{ backgroundColor: 'green' }} 
                                            onClick={handleEnable}
                                        >
                                            Enable Component
                                        </Button>
                                    }
                                </Grid>
                            )}
                            <Grid item xs={12} />
                            <Grid item xs={12} sm={9}>
                                <Button 
                                    variant="contained" 
                                    size="normal" 
                                    style={style.buttonSave} 
                                    sx={{ backgroundColor: 'green', mr: '20px' }} 
                                    onClick={handleSubmit}
                                >
                                    Save Part Component
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    size="normal" 
                                    style={style.button} 
                                    onClick={() => navigate('/partComponents')}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                        </StyledMainGrid>
                    </form>
                </StyledCardFormContent>
            </StyledCard>
            {!props.add && <PartComponentStatisticDialog open={openStatistic} onClose={handleCloseStatistic} partComponent={component}/>}
        </Page>
    );
}
